import { FC, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, styled } from '@mui/material';
import classNames from 'classnames';

import { Chip, Menu } from '@components/base';
import {
  LogicalOperator,
  LogicalOperatorColorMap,
  LogicalOperators,
} from '@models/FilterDefinition';

const CombinatorWrapper = styled(Box)`
  min-height: 100%;
  display: flex;
  align-items: center;
  position: relative;
  padding: ${({ theme }) => theme.spacing(0, 1)};
  margin-left: ${({ theme }) => theme.spacing(-1)};

  &.edge {
    margin-left: ${({ theme }) => theme.spacing(-3)};
  }

  &::before {
    content: '';
    top: 32px;
    border: ${({ theme }) => `1px solid ${theme.palette.action.disabled}`};
    border-right: none;
    border-top-left-radius: ${({ theme }) => Number(theme.shape.borderRadius) * 2}px;
    border-bottom-left-radius: ${({ theme }) => Number(theme.shape.borderRadius) * 2}px;
    height: calc(100% - 64px);
    position: absolute;
    left: 50%;
    width: 50%;
  }
`;

type WithCombinatorMenuProps = PropsWithChildren<{
  disabled?: boolean;
  edge?: boolean;
  hideMenu?: boolean;
  onChange: (combinator: LogicalOperator) => void;
  value: LogicalOperator;
}>;

const WithCombinatorMenu: FC<WithCombinatorMenuProps> = ({
  children,
  disabled,
  edge,
  hideMenu,
  onChange,
  value,
}) => {
  const { t } = useTranslation();

  return (
    <Stack direction='row' display='flex' width={1}>
      {!hideMenu && (
        <CombinatorWrapper className={classNames({ edge })}>
          {disabled ? (
            <Chip
              color={LogicalOperatorColorMap[value]}
              label={t('combinator', { context: value })}
              size='small'
            />
          ) : (
            <Menu>
              <Menu.ChipButton
                color={LogicalOperatorColorMap[value]}
                hideExpandIcon
                label={t('combinator', { context: value })}
                size='small'
              />

              <Menu.Content>
                {LogicalOperators.map(c => (
                  <Menu.Item key={c} onClick={() => onChange(c)} selected={value === c}>
                    {t('combinator', { context: c })}
                  </Menu.Item>
                ))}
              </Menu.Content>
            </Menu>
          )}
        </CombinatorWrapper>
      )}

      {children}
    </Stack>
  );
};

export default WithCombinatorMenu;
